/**
 * Founder-facing words and outside links in one place. Brand rules (docs/brand.md): plain, kind, no vendor names
 * (Paseo is named where they install it, Claude and Shopify because they bring them), no em dashes.
 */

/** Where to get Paseo: the store listings and the desktop download, as paseo.sh links them (2026-10-11). */
export const PASEO_LINKS = [
  { label: 'iPhone and iPad', href: 'https://apps.apple.com/app/paseo-pocket-engineer/id6758887924' },
  { label: 'Android', href: 'https://play.google.com/store/apps/details?id=sh.paseo' },
  { label: 'Mac, Windows, Linux', href: 'https://paseo.sh/download' },
] as const

/** Paseo on the web, for "Open Paseo" when there's no app link to hand. */
export const PASEO_WEB = 'https://app.paseo.sh'

export const CLAUDE_PLANS = 'https://claude.com/pricing'

/**
 * The first request in setup: it needs only Shopify, so it works the moment the store is connected. The starter's
 * Today screen reads the store once the keys arrive; the agent restarts the preview and says what is still example.
 */
export const FIRST_ASK = {
  tag: 'your real numbers',
  short: 'Put my real Shopify numbers on Today',
  full: "I've connected Shopify. Switch my Today screen from example data to my real numbers, send me the preview link, and tell me anything that's still on example data.",
}

/**
 * Three last-mile prompts (docs/boards/messaging-r1.md). Each needs more than Shopify (retailers, lead times, a
 * co-packer's minimum), so they come at the end of setup, once the first screen is live (wet run, 2026-10-14).
 */
export const LATER_ASKS = [
  {
    tag: 'a retail map',
    short: 'Map my retailers against my Shopify customers by zip code',
    full: 'Show me a map of my independent retailers against my Shopify customers by zip code, and flag the zip codes where we should pitch a store.',
  },
  {
    tag: 'a Monday routine',
    short: "Every Monday, list who hasn't reordered in six weeks",
    full: "Every Monday, list the retailers who haven't reordered in six weeks and draft a note from me to each.",
  },
  {
    tag: 'a production plan',
    short: 'When should I book the next co-packer run?',
    full: 'Tell me when to book the next co-packer run, using our real lead times and their 2,400-case minimum.',
  },
]

export type Ask = typeof FIRST_ASK

/** The public address for a founder's app: the app's domain, else their subdomain. */
export function addressOf(world: { founder: { subdomain: string | null }; app: { domain: string | null } | null }): string | null {
  const host = world.app?.domain ?? (world.founder.subdomain ? `${world.founder.subdomain}.cpg.computer` : null)
  return host ? host.replace(/^https?:\/\//, '').replace(/\/+$/, '') : null
}
